import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useAppSelector } from '@/store/hooks';
import { canRelease } from '@/types';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { ErrorState } from '@/components/common';
import { formatBytes, formatDateTime } from '@/shared/lib/format';
import { ROUTES } from '@/constants';
import { EmailLabsDialog } from './EmailLabsDialog';
import { useRelease, useReleaseNow, useUpdateRelease, useWithdrawRelease } from './queries';
import { PLATFORM_LABEL, type Release, type UpdateReleaseRequest } from './types';

const STATUS_TONE = { draft: 'inert', released: 'positive', withdrawn: 'negative' } as const;
const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;
const TEXTAREA =
  'w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60';

/**
 * One release: what labs read about it, its builds, and the decision to offer
 * it.
 *
 * The words (title, summary, notes, known issues) and whether it is required
 * can be changed at any time; the desktop's update prompt and the release
 * email read them as they are. Release puts it on the update check for its
 * channel; Withdraw takes it off (installed machines keep it). Email labs is
 * offered once a stable release is out.
 */
export function ReleaseDetail({ releaseId }: { releaseId: string }) {
  const release = useRelease(releaseId);

  if (release.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-7 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (release.isError) {
    return (
      <ErrorState
        title="Could not load this release"
        description={release.error.message}
        action={
          <Button asChild variant="outline">
            <Link to={ROUTES.releases}>Back to releases</Link>
          </Button>
        }
      />
    );
  }
  return <Loaded release={release.data} />;
}

function Loaded({ release }: { release: Release }) {
  const role = useAppSelector((state) => state.auth.user?.role);
  const mayChange = canRelease(role);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={ROUTES.releases}
          className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> All releases
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-mono text-xl font-semibold">{release.versionNumber}</h2>
            <StatusPill status={release.releaseChannel} />
            <StatusPill status={release.status} tone={STATUS_TONE[release.status]} />
          </div>
          {mayChange && <Actions release={release} />}
        </div>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          {release.status === 'draft' &&
            'Draft: desktops are not offered this yet. Fill in the details, then Release.'}
          {release.status === 'released' &&
            `Offered to ${release.releaseChannel} desktops since ${release.releasedAt ? formatDateTime(release.releasedAt) : '—'}${release.releasedBy ? ` (released by ${release.releasedBy.name})` : ''}.`}
          {release.status === 'withdrawn' &&
            'Withdrawn: no longer offered. Machines that installed it keep it. Release it again to bring it back.'}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <DetailsForm release={release} disabled={!mayChange} />
        <aside className="space-y-4 lg:self-start">
          <BuildsCard release={release} />
          <div className="rounded-lg border bg-card p-4 text-[12.5px]">
            <p className="text-muted-foreground">Created</p>
            <p className="font-medium">{formatDateTime(release.createdAt)}</p>
            <p className="mt-3 text-muted-foreground">Last changed</p>
            <p className="font-medium">
              {formatDateTime(release.updatedAt)}
              {release.updatedBy ? ` by ${release.updatedBy.name}` : ' by CI'}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Actions({ release }: { release: Release }) {
  const releaseNow = useReleaseNow(release.id);
  const withdraw = useWithdrawRelease(release.id);
  const [open, setOpen] = useState(false);
  const goingOut = release.status !== 'released';
  const action = goingOut ? releaseNow : withdraw;
  const noBuilds = release.builds.length === 0;
  const unsigned = release.builds
    .filter((b) => !b.codeSigned)
    .map((b) => PLATFORM_LABEL[b.platform]);

  return (
    <div className="flex flex-wrap gap-2">
      {release.status === 'released' && release.releaseChannel === 'stable' && (
        <EmailLabsDialog release={release} />
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant={goingOut ? 'default' : 'outline'} disabled={goingOut && noBuilds}>
            {goingOut ? (release.status === 'withdrawn' ? 'Release again' : 'Release') : 'Withdraw'}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {goingOut
                ? `Release ${release.versionNumber}?`
                : `Withdraw ${release.versionNumber}?`}
            </DialogTitle>
            <DialogDescription>
              {goingOut
                ? `Every ${release.releaseChannel} desktop is offered it from its next update check${release.mandatory ? ', and must install it - it is marked required' : ''}.`
                : 'It stops being offered. Machines that already installed it keep it.'}
            </DialogDescription>
          </DialogHeader>
          {goingOut && unsigned.length > 0 && (
            <p className="text-sm text-destructive">
              Not code-signed: {unsigned.join(', ')}. Windows will warn labs before installing.
            </p>
          )}
          {goingOut && !release.title && (
            <p className="text-sm text-muted-foreground">
              It has no title yet. Labs see the title in the update prompt and the email.
            </p>
          )}
          {action.isError && <p className="text-sm text-destructive">{action.error.message}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={goingOut ? 'default' : 'destructive'}
              disabled={action.isPending}
              onClick={() => {
                action.mutate(undefined, {
                  onSuccess: () => {
                    setOpen(false);
                  },
                });
              }}
            >
              {goingOut ? 'Release' : 'Withdraw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface FormValues {
  title: string;
  summary: string;
  releaseNotes: string;
  knownIssues: string;
  mandatory: boolean;
  minSupportedVersion: string;
}

function valuesOf(release: Release): FormValues {
  return {
    title: release.title ?? '',
    summary: release.summary ?? '',
    releaseNotes: release.releaseNotes ?? '',
    knownIssues: release.knownIssues ?? '',
    mandatory: release.mandatory,
    minSupportedVersion: release.minSupportedVersion ?? '',
  };
}

function DetailsForm({ release, disabled }: { release: Release; disabled: boolean }) {
  const update = useUpdateRelease(release.id);
  const saved = valuesOf(release);
  // A draft of the form, reset whenever the saved release changes underneath.
  const [draft, setDraft] = useState<{ base: string; values: FormValues }>({
    base: release.updatedAt,
    values: saved,
  });
  const values = draft.base === release.updatedAt ? draft.values : saved;
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setDraft({ base: release.updatedAt, values: { ...values, [key]: value } });
  };

  const minInvalid =
    values.minSupportedVersion.trim() !== '' && !SEMVER.test(values.minSupportedVersion.trim());

  const changes: UpdateReleaseRequest = {};
  for (const key of [
    'title',
    'summary',
    'releaseNotes',
    'knownIssues',
    'minSupportedVersion',
  ] as const) {
    if (values[key].trim() !== saved[key].trim()) changes[key] = values[key].trim() || null;
  }
  if (values.mandatory !== saved.mandatory) changes.mandatory = values.mandatory;
  const dirty = Object.keys(changes).length > 0;

  return (
    <form
      className="space-y-4 rounded-lg border bg-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (dirty && !minInvalid) update.mutate(changes);
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="release-title">Title</Label>
        <Input
          id="release-title"
          value={values.title}
          maxLength={120}
          disabled={disabled}
          placeholder="e.g. Faster margins and new zirconia shades"
          onChange={(e) => {
            set('title', e.target.value);
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="release-summary">Summary</Label>
        <Input
          id="release-summary"
          value={values.summary}
          maxLength={300}
          disabled={disabled}
          placeholder="One line for lists and the release email"
          onChange={(e) => {
            set('summary', e.target.value);
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="release-notes">Release notes</Label>
        <textarea
          id="release-notes"
          rows={8}
          value={values.releaseNotes}
          maxLength={10_000}
          disabled={disabled}
          placeholder="What's new and what's fixed. Shown in NuDent's update prompt and the release email."
          className={TEXTAREA}
          onChange={(e) => {
            set('releaseNotes', e.target.value);
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="release-known">Known issues</Label>
        <textarea
          id="release-known"
          rows={3}
          value={values.knownIssues}
          maxLength={5_000}
          disabled={disabled}
          placeholder="Anything labs should know before updating"
          className={TEXTAREA}
          onChange={(e) => {
            set('knownIssues', e.target.value);
          }}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5">
          <div>
            <Label htmlFor="release-mandatory">Required update</Label>
            <p className="text-xs text-muted-foreground">
              Desktops must install it to keep working.
            </p>
          </div>
          <Switch
            id="release-mandatory"
            checked={values.mandatory}
            disabled={disabled}
            onCheckedChange={(next) => {
              set('mandatory', next);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="release-min">Minimum supported version</Label>
          <Input
            id="release-min"
            value={values.minSupportedVersion}
            disabled={disabled}
            placeholder="e.g. 1.2.0 - older must update"
            aria-invalid={minInvalid}
            onChange={(e) => {
              set('minSupportedVersion', e.target.value);
            }}
          />
          {minInvalid && <p className="text-xs text-destructive">Use a version like 1.2.0</p>}
        </div>
      </div>
      {update.isError && <p className="text-sm text-destructive">{update.error.message}</p>}
      {!disabled && (
        <div className="flex gap-2">
          <Button type="submit" disabled={!dirty || minInvalid || update.isPending}>
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!dirty || update.isPending}
            onClick={() => {
              setDraft({ base: release.updatedAt, values: saved });
            }}
          >
            Discard changes
          </Button>
        </div>
      )}
    </form>
  );
}

function BuildsCard({ release }: { release: Release }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-[12.5px] font-medium">Builds</p>
      {release.builds.length === 0 ? (
        <p className="mt-2 text-[12.5px] text-muted-foreground">
          None yet. CI adds the Windows and Linux builds as it registers them.
        </p>
      ) : (
        <ul className="mt-2 space-y-3">
          {release.builds.map((build) => (
            <li key={build.id} className="text-[12.5px]">
              <div className="flex items-center gap-1.5 font-medium">
                {build.codeSigned ? (
                  <ShieldCheck size={14} className="text-pill-positive-foreground" />
                ) : (
                  <ShieldAlert size={14} className="text-pill-caution-foreground" />
                )}
                {PLATFORM_LABEL[build.platform]}
                <span className="font-normal text-muted-foreground">
                  · {formatBytes(Number(build.fileSizeBytes))}
                  {build.codeSigned ? '' : ' · unsigned'}
                </span>
              </div>
              <p className="text-muted-foreground">
                Registered {formatDateTime(build.publishedAt)}
                {build.publishedBy === null ? ' by CI' : ''}
              </p>
              <p
                className="truncate font-mono text-[11px] text-muted-foreground"
                title={build.fileHashSha256}
              >
                sha256 {build.fileHashSha256.slice(0, 16)}…
              </p>
              <a
                href={build.cloudfrontUrl}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Installer
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
